import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Edit3, X, Save, Heart } from "lucide-react";

const empty = () => ({ id: null, tier: "digital", name: "", description: "", price: 27, category: "General", download_url: "#" });

const CATEGORIES = ["General", "Acquisition", "Operations", "Pricing", "Marketing", "Guest Experience", "Finance", "Legal"];

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [draft, setDraft] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      await api.post("/admin/products", draft);
      toast.success("Product saved.");
      setDraft(null);
      load();
    } catch { toast.error("Save failed."); }
  };
  const del = async (id) => {
    if (!window.confirm("Delete this product?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Deleted.");
    load();
  };

  const filtered = products.filter((p) => filter === "all" ? true : p.tier === filter);

  return (
    <div className="text-white">
      <div className="flex justify-between items-end mb-8">
        <div>
          <div className="uppercase tracking-[0.28em] text-xs text-brand-pink mb-3">Products</div>
          <h1 className="font-display text-4xl">Everything for sale.</h1>
          <p className="text-white/60 mt-2 text-sm">Free lead magnets, digital products, cold-call scripts, checklists, and playbooks.</p>
        </div>
        <Button onClick={() => setDraft(empty())} data-testid="admin-new-product" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white">
          <Plus className="w-4 h-4 mr-2" /> New product
        </Button>
      </div>

      <div className="flex gap-2 mb-6">
        {[
          { id: "all", label: `All (${products.length})` },
          { id: "free", label: `Free (${products.filter(p => p.tier === "free").length})` },
          { id: "digital", label: `Digital (${products.filter(p => p.tier === "digital").length})` },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            data-testid={`admin-prod-tab-${t.id}`}
            className={`px-5 py-2 rounded-full text-sm transition ${filter === t.id ? "bg-brand-pink text-white" : "bg-white/5 text-white/70 hover:bg-white/10"}`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="rounded-3xl bg-white/5 border border-white/10 divide-y divide-white/5">
        {filtered.map((p) => (
          <div key={p.id} data-testid={`admin-product-${p.id}`} className="p-5 flex items-center gap-4">
            <div className={`w-10 h-10 rounded-2xl flex items-center justify-center flex-shrink-0 ${p.tier === "free" ? "bg-brand-blush text-brand-pink" : "bg-brand-pink/20 text-brand-pink"}`}>
              <Heart className="w-4 h-4" fill="#E91E63" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display text-lg truncate">{p.name}</div>
              <div className="text-sm text-white/60 truncate">{p.description}</div>
              <div className="text-xs text-white/40 mt-1 flex gap-3">
                <span className="uppercase tracking-widest">{p.tier}</span>
                {p.category && <span>· {p.category}</span>}
              </div>
            </div>
            <div className="font-display text-xl text-brand-pink w-20 text-right">{p.price === 0 ? "Free" : `$${p.price}`}</div>
            <Button variant="ghost" onClick={() => setDraft(p)} data-testid={`admin-prod-edit-${p.id}`} className="text-white hover:bg-white/10 rounded-full"><Edit3 className="w-4 h-4" /></Button>
            <Button variant="ghost" onClick={() => del(p.id)} data-testid={`admin-prod-del-${p.id}`} className="text-brand-pink hover:bg-brand-pink/10 rounded-full"><Trash2 className="w-4 h-4" /></Button>
          </div>
        ))}
        {filtered.length === 0 && <div className="p-10 text-white/50 text-center">Nothing here yet.</div>}
      </div>

      {draft && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur overflow-y-auto py-10 px-4">
          <div className="max-w-2xl mx-auto rounded-3xl bg-neutral-900 border border-white/10 p-8" data-testid="admin-product-editor">
            <div className="flex justify-between mb-6">
              <div className="font-display text-2xl">{draft.id ? "Edit product" : "New product"}</div>
              <button onClick={() => setDraft(null)} className="text-white/60 hover:text-white"><X className="w-5 h-5" /></button>
            </div>

            <div className="space-y-4">
              <div>
                <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Name</Label>
                <Input data-testid="admin-prod-name" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} className="rounded-full h-11 bg-white/5 border-white/10 text-white" />
              </div>
              <div>
                <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Short description</Label>
                <Textarea data-testid="admin-prod-desc" rows={3} value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} className="rounded-2xl bg-white/5 border-white/10 text-white" />
              </div>
              <div className="grid md:grid-cols-3 gap-4">
                <div>
                  <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Tier</Label>
                  <select
                    data-testid="admin-prod-tier"
                    value={draft.tier}
                    onChange={(e) => setDraft({ ...draft, tier: e.target.value })}
                    className="w-full rounded-full h-11 bg-white/5 border border-white/10 text-white px-4"
                  >
                    <option value="free">Free (lead magnet)</option>
                    <option value="digital">Digital (paid)</option>
                  </select>
                </div>
                <div>
                  <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Price ($)</Label>
                  <Input
                    data-testid="admin-prod-price"
                    type="number"
                    disabled={draft.tier === "free"}
                    value={draft.price ?? 0}
                    onChange={(e) => setDraft({ ...draft, price: Number(e.target.value || 0) })}
                    className="rounded-full h-11 bg-white/5 border-white/10 text-white disabled:opacity-40"
                  />
                </div>
                <div>
                  <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Category</Label>
                  <select
                    data-testid="admin-prod-cat"
                    value={draft.category || "General"}
                    onChange={(e) => setDraft({ ...draft, category: e.target.value })}
                    className="w-full rounded-full h-11 bg-white/5 border border-white/10 text-white px-4"
                  >
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <Label className="mb-2 block text-white/80 text-xs uppercase tracking-widest">Download URL (PDF / Notion / Drive link)</Label>
                <Input data-testid="admin-prod-url" value={draft.download_url || "#"} onChange={(e) => setDraft({ ...draft, download_url: e.target.value })} className="rounded-full h-11 bg-white/5 border-white/10 text-white" placeholder="https://..." />
              </div>
            </div>

            <div className="flex justify-end gap-3 mt-8">
              <Button variant="ghost" onClick={() => setDraft(null)} className="text-white/70">Cancel</Button>
              <Button onClick={save} data-testid="admin-save-product" className="rounded-full bg-brand-pink hover:bg-brand-pink/90 text-white"><Save className="w-4 h-4 mr-2" /> Save product</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
